import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import { getLocalDate } from "../../utils/TextUtils.js";

function AggregationForm() {
    const [aggregation, setAggregation] = useState({
        route_id: "",
        litres: "",
        collection_date: "",
        shift: "",
        notes: ""
    });

    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isEditing, setIsEditing] = useState(false);
    const [routes, setRoutes] = useState([]);
    const navigate = useNavigate();
    const { id } = useParams();
    const token = localStorage.getItem("access_token");
    const shifts = ["Morning", "Evening"];
    
    useEffect(() => {
        const fetchAggregation = async () => {
            if (id) {
                try {
                    const response = await fetch(`${API_URL}/route-aggregations/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        // Handle both response formats
                        const aggregationData = data.aggregation || data;
                        
                        // Parse the date from ISO string to YYYY-MM-DD format
                        const rawDate = aggregationData.collection_date;
                        let formattedDate = "";
                        
                        if (rawDate) {
                            const date = new Date(rawDate);
                            // Format to YYYY-MM-DD
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            formattedDate = `${year}-${month}-${day}`;
                        }
                        
                        setAggregation({
                            route_id: aggregationData.route_id || "",
                            litres: aggregationData.litres || "",
                            collection_date: formattedDate, // Use formatted date
                            shift: aggregationData.shift || "",
                            notes: aggregationData.notes || ""
                        });
                        setIsEditing(true);
                    } else if (response.status === 404) {
                        setNotification({ type: "danger", message: "Route aggregation not found." });
                    } else if (response.status === 401) {
                        navigate("/logout");
                    } else {
                        throw new Error("Failed to fetch route aggregation.");
                    }
                } catch (error) {
                    setNotification({ type: "danger", message: "An error occurred while fetching the route aggregation." });
                }
            }
        };

        const fetchRoutes = async () => {
            try {
                const response = await fetch(`${API_URL}/collection-routes`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setRoutes(data || []);
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    throw new Error("Failed to fetch routes.");
                }
            } catch (error) {
                console.error("Error fetching routes:", error);
            }
        };

        const fetchData = async () => {
            setLoading(true);
            await Promise.all([
                fetchAggregation(),
                fetchRoutes()
            ]);
            setLoading(false);
        };

        fetchData();
    }, [id, navigate, token]);

    // Helper function to format date for input field
    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        
        // If it's already in YYYY-MM-DD format, return as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }
        
        // Parse ISO or other date formats
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return "";
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setAggregation((prevAggregation) => ({
            ...prevAggregation,
            [name]: value,
        }));
        // Clear error for this field
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const handleTextareaChange = (event) => {
        const { name, value } = event.target;
        setAggregation((prevAggregation) => ({
            ...prevAggregation,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            const method =  "POST";
            const url = isEditing ?
                `${API_URL}/route-aggregations/${id}/edit` :
                `${API_URL}/route-aggregations`;

            // Format the data for submission
            const formData = new FormData();
            formData.append('route_id', aggregation.route_id);
            formData.append('litres', parseFloat(aggregation.litres) || 0);
            formData.append('collection_date', aggregation.collection_date);
            formData.append('shift', aggregation.shift);
            if (aggregation.notes) {
                formData.append('notes', aggregation.notes);
            }

            const response = await fetch(url, {
                method,
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json",
                },
                body: formData,
            });

            if (response.ok) {
                setNotification({
                    message: isEditing ?
                        "Route aggregation updated successfully!" :
                        "Route aggregation created successfully!",
                    type: "success"
                });

                // Navigate after a short delay to show success message
                setTimeout(() => {
                    navigate(`/dashboard/aggregations`);
                }, 1500);

            } else if (response.status === 401) {
                navigate("/logout");
            } else if (response.status === 422) {
                // Handle validation errors
                const errorData = await response.json();
                if (errorData.errors) {
                    setErrors(errorData.errors);
                }
                setNotification({
                    message: errorData.message || "Please fix the errors below.",
                    type: "danger"
                });
            } else {
                const errorData = await response.json();
                setNotification({
                    message: errorData.message || "An error occurred while saving the route aggregation.",
                    type: "danger"
                });
            }
        } catch (error) {
            setNotification({
                message: "An error occurred while saving the route aggregation.",
                type: "danger"
            });
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    if (loading) return <p>Loading...</p>;

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">
                            {isEditing ? "Update Route Aggregation" : "Add Route Aggregation"}
                        </p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/aggregations" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back to List
                        </Link>
                    </p>
                </div>
            </nav>

            <div className="column dash-section">
                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <form onSubmit={handleSubmit}>
                    <div className="columns">
                        <div className="column is-half">
                            {/* Route Selection */}
                            <div className="field">
                                <label className="label">Collection Route *</label>
                                <div className="control">
                                    <div className={`select is-fullwidth ${errors.route_id ? "is-danger" : ""}`}>
                                        <select
                                            name="route_id"
                                            value={aggregation.route_id}
                                            onChange={handleChange}
                                            required
                                            disabled={isEditing}
                                        >
                                            <option value="">Select a route</option>
                                            {routes.map((route) => (
                                                <option key={route.id} value={route.id}>
                                                    {route.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                {errors.route_id && errors.route_id.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Collection Date */}
                            <div className="field">
                                <label className="label">Collection Date *</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.collection_date ? "is-danger" : ""}`}
                                        type="date"
                                        name="collection_date"
                                        value={aggregation.collection_date} // Direct value
                                        onChange={handleChange}
                                        required
                                        max={getLocalDate()}
                                        disabled={isEditing}
                                    />
                                </div>
                                {errors.collection_date && errors.collection_date.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Shift Selection */}
                            <div className="field">
                                <label className="label">Shift *</label>
                                <div className="control">
                                    <div className={`select is-fullwidth ${errors.shift ? "is-danger" : ""}`}>
                                        <select
                                            name="shift"
                                            value={aggregation.shift}
                                            onChange={handleChange}
                                            required
                                            disabled={isEditing}
                                        >
                                            <option value="">Select a shift</option>
                                            {shifts.map((shift, index) => (
                                                <option key={index} value={shift}>
                                                    {shift}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                {errors.shift && errors.shift.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Litres */}
                            <div className="field">
                                <label className="label">Total Litres *</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.litres ? "is-danger" : ""}`}
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        name="litres"
                                        value={aggregation.litres}
                                        onChange={handleChange}
                                        placeholder="Enter total litres collected"
                                        required
                                    />
                                </div>
                                {errors.litres && errors.litres.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Notes */}
                            <div className="field">
                                <label className="label">Notes</label>
                                <div className="control">
                                    <textarea
                                        className={`textarea ${errors.notes ? "is-danger" : ""}`}
                                        name="notes"
                                        value={aggregation.notes}
                                        onChange={handleTextareaChange}
                                        placeholder="Additional notes about this aggregation..."
                                        rows="3"
                                    />
                                </div>
                                {errors.notes && errors.notes.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Display warning if editing unique fields */}
                            {isEditing && (
                                <article className="message is-warning">
                                    <div className="message-body">
                                        <strong>Note:</strong> Route, Date, and Shift cannot be changed
                                        after creation to maintain data integrity. Only litres and notes can be updated.
                                    </div>
                                </article>
                            )}

                            {/* Duplicate aggregation warning */}
                            {errors.shift && errors.shift.includes('Duplicate entry') && (
                                <article className="message is-danger">
                                    <div className="message-body">
                                        <strong>Error:</strong> An aggregation already exists for this route, date, and shift.
                                        Please select a different combination or edit the existing record.
                                    </div>
                                </article>
                            )}
                        </div>

                        {/* Right column for additional info/help */}
                        <div className="column is-half">
                            <article className="message is-info">
                                <div className="message-header">
                                    <p><i className="fas fa-info-circle"></i> &nbsp; Information</p>
                                </div>
                                <div className="message-body">
                                    <p><strong>Route Aggregation is...</strong></p>
                                    <p>
                                        This is the sum total of milk collected from all farmers in a specific route
                                        after it's been brought to the collection/aggregation center and measured again.
                                    </p>
                                    <br />
                                    <p>This is used to compare against the total milk collected from farmers to identify discrepancies,
                                        losses, or issues in the collection process.
                                    </p>
                                    <br />
                                    <p>
                                        The aggregation is done per route, per date, and per shift (morning/evening).
                                    </p>
                                </div>
                            </article>

                            {/* Current route info if editing */}
                            {isEditing && aggregation.route_id && (
                                <article className="message is-light">
                                    <div className="message-header">
                                        <p><i className="fas fa-route"></i> &nbsp; Route Information</p>
                                    </div>
                                    <div className="message-body">
                                        {(() => {
                                            const route = routes.find(r => r.id == aggregation.route_id);
                                            return route ? (
                                                <>
                                                    <p><strong>Route:</strong> {route.name}</p>
                                                    <p><strong>Code:</strong> {route.code || 'N/A'}</p>
                                                    <p><strong>Description:</strong> {route.description || 'No description'}</p>
                                                    <hr />
                                                    <Link
                                                        to={`/dashboard/routes/${route.id}`}
                                                        className="button is-small is-info"
                                                    >
                                                        View Route Details
                                                    </Link>
                                                </>
                                            ) : null;
                                        })()}
                                    </div>
                                </article>
                            )}
                        </div>
                    </div>

                    <div className="columns">
                        <div className="column is-half">
                            <div className="field">
                                <div className="control">
                                    <button
                                        type="submit"
                                        className="button primary-bg is-pulled-right is-fullwidth"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="icon">
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                </span>
                                                <span>&nbsp; Processing...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="icon">
                                                    <i className={isEditing ? "fas fa-save" : "fas fa-check"}></i>
                                                </span>
                                                <span>&nbsp; {isEditing ? "UPDATE AGGREGATION" : "SAVE AGGREGATION"}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </main>
    );
}

export default AggregationForm;